import { Images } from "@/constants/images"
import { getImageUrl } from "@/lib/get-image-url"
import { Image } from "@/types/tour"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { ADMIN_API_CONFIG } from "../constants/api-configs"
import { DefaultImage } from "./external-components"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const replaceImageUrls = (content: string) => {
  const currentDomain = process.env.NEXT_PUBLIC_OLD_IMAGE_URL
  const newDomain = process.env.NEXT_PUBLIC_URL_IMAGES_PRODUCTION

  if (!currentDomain || !newDomain) {
    throw new Error("Environment variables CURRENT and DOMAIN must be set")
  }

  const regex = new RegExp(`${currentDomain.replace(/\/$/, "")}(/?)`, "g")

  const updatedContent = content.replace(regex, `${newDomain}/`)

  return updatedContent
}

export const dateFormat = (date: string, locale: string): string => {
  const parsedDate = new Date(date)

  if (isNaN(parsedDate.getTime())) {
    throw new Error("Invalid date format")
  }

  return parsedDate.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

export const formatDate = (createdAt: string | Date) => {
  const date = new Date(createdAt)
  const day = String(date.getUTCDate()).padStart(2, "0")
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const year = date.getUTCFullYear()
  const hours = String(date.getUTCHours()).padStart(2, "0")
  const minutes = String(date.getUTCMinutes()).padStart(2, "0")
  const seconds = String(date.getUTCSeconds()).padStart(2, "0")

  const formattedTime = `${hours}:${minutes}:${seconds}`
  const formattedDate = `${day}/${month}/${year}`
  return { formattedDate, formattedTime }
}

export function generateImagePath(
  inputPath: string | null | undefined | any,
  sizeType: "thumbnail" | "small" | "medium" | "large" | "original",
) {
  if (inputPath && typeof inputPath === "object") {
    const extractedUrl = inputPath.url || inputPath.path || inputPath.src || null
    inputPath = extractedUrl
  }

  // Validate inputPath is a valid string
  if (!inputPath || typeof inputPath !== "string") {
    return DefaultImage
  }

  // Define allowed size types
  const allowedSizeTypes = ["thumbnail", "small", "medium", "large", "original"]

  if (sizeType === "original") {
    const result = `${ADMIN_API_CONFIG.API_URL}${inputPath}`
    return result
  }

  // Validate sizeType
  if (!allowedSizeTypes.includes(sizeType)) {
    throw new Error(`Invalid size type. Allowed values are: ${allowedSizeTypes.join(", ")}`)
  }

  // Try-catch to handle errors and fallback to original if needed
  try {
    // Extract the filename from the input path
    const filename = inputPath.split("/").pop() as string

    // Construct the new path with the size type
    const result = `${ADMIN_API_CONFIG.API_URL}${inputPath.replace(filename, `${sizeType}_${filename}`)}`
    return result
  } catch (error) {
    // If any error occurs, return the original image
    return `${ADMIN_API_CONFIG.API_URL}${inputPath}`
  }
}

export const calcAvgStatsTour = (day?: number, distance?: number, percent?: boolean) =>
  percent ? ((distance ?? 0) / (day ?? 1) / (distance ?? 0)) * 100 : (distance ?? 0) / (day ?? 1)

export const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

export const updateImageSrc = (src: string | undefined) => {
  if (!src || typeof src !== "string" || src?.trim() === "") return Images.imageEmpty.src

  return `${ADMIN_API_CONFIG.API_URL}${src}`
}

export const updateImageSrcArray = (src?: (string | Image)[]) => {
  return (() => {
    const img = src
    if (!img) return []
    // Nếu là string, convert thành array
    if (typeof img === "string") return [img]
    // Nếu là array, map và xử lý từng phần tử
    if (Array.isArray(img)) {
      return img.map(item => (typeof item === "string" ? item : (item as { url?: string }).url || item))
    }
    if (typeof img === "object") return [(img as { url?: string }).url || ""]
    return []
  })()
}

// Helper function to get image URL
export const getImageSrc = (imageUrl: string | { url: string; alternativeText?: string } | undefined): string => {
  if (!imageUrl) return DefaultImage
  const url = getImageUrl(imageUrl)
  return url.startsWith("http") ? url : `${ADMIN_API_CONFIG.API_URL}${url}`
}

// Helper function to get alt text
export const getImageAlt = (
  imageUrl: string | { url: string; alternativeText?: string } | undefined,
  fallback: string,
): string => {
  if (typeof imageUrl === "object" && imageUrl?.alternativeText) {
    return imageUrl.alternativeText
  }
  return fallback
}
